import { mdiTune } from '@mdi/js';
import Icon from '@mdi/react';
import get from 'lodash/get';
import React, { useEffect, useRef, useState } from 'react';
import { isMobileOnly as isMobile } from 'react-device-detect';
import { useInView } from 'react-intersection-observer';
import { connect, useSelector } from 'react-redux';
import { loadMoreInteraction } from '../../../helpers/datalayer-utils';
import { BQLink } from '../../atoms/bqlink';
import { widgetLink } from '../../../helpers/utils';
import Loader from '../../atoms/icons/loader';
import NotificationActiveIcon from '../../atoms/icons/notification-active';
import SearchIcon from '../../atoms/icons/search';
import { ArrowRightIcon, CloseIcon } from '../../atoms/material-icons';
import BudgetStrip2023 from '../../collection-templates/budget-strip-2023';
import MarketTicker from '../../rows/header/market-ticker';
import { makeApiCall, isWithinTradingHours } from '../../../helpers/utils';
import { useInterval } from '../../../helpers/custom-hooks';
import './breakout-alerts.m.css';

const BreakoutAlerts = ({
  widget = false,
  breakout_alerts,
  widgetRows = 3,
  setShowBreakoutWidget = null,
  statsWidget = false,
  darkMode = false,
  lightMode = false,
  customString = '',
  customClass = '',
  hideCta = false,
  setShowMoreButton = null,
  fullWidget = false,
  isWeb = false,
}) => {
  const isWidget = isWeb ? false : widget;
  const widgetParam = isWidget ? '?view=apps' : '';
  const [currentFilterTab, setCurrentFilterTab] = useState('entity');
  const [currentFilter, setCurrentFilter] = useState({ id: 0, name: 'All' });
  const [allStocks, setAllStocks] = useState([]);
  const [allDataFilters, setAllDataFilters] = useState([]);
  const [allFilters, setAllFilters] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [showMobileFilterButton, setShowMobileFilterButton] = useState(true);
  const [showNoDataImage, setShowNoDataImage] = useState(false);
  const [showNoFilterDataImage, setShowNoFilterDataImage] = useState(false);
  const [disableApply, setDisableApply] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [breakoutParams, setBreakoutParams] = useState('');
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });
  const userData = useSelector((state) => get(state, ['userData']));
  const pageType = useSelector((state) => get(state, ['qt', 'pageType']));
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  const inputRefDesktop = useRef();
  const inputRefFilterDesktop = useRef();

  useEffect(() => {
    const footerElement = document.getElementById('footer');
    if (footerElement && isMobile) {
      const windowHeight = window.innerHeight;
      const footerRect = footerElement.getBoundingClientRect();
      const footerPosition = windowHeight * 0.8;
      setShowMobileFilterButton(footerRect.top > footerPosition);
    }
  }, [scrollY]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollY]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const customBreakout = urlParams.get('customString') || '';
    if (!!customBreakout) {
      setBreakoutParams(customBreakout);
    }
  }, []);

  useEffect(() => {
    if (initialLoading && inView) {
      setInitialLoading(false);
      fetchFilters();
      fetchBreakoutData(0, '', customString || breakoutParams);
    }
  }, [inView]);

  useInterval(() => {
    if (isWithinTradingHours(tradingHolidays, specialTradingDays) && (!widget || inView)) {
      checkCurrentStatusAndFetch(customString || breakoutParams);
    }
  });

  const fetchFilters = () => {
    if (!widget) {
      Promise.all([
        makeApiCall('/foapi/market/getMarketEvent'),
        makeApiCall('/foapi/market/getSector'),
      ]).then((res) => {
        if (res[0].status && res[1].status) {
          setAllDataFilters([
            {
              id: 'entity',
              name: 'Entity Type',
              fields: res[0].data,
            },
            {
              id: 'sectors',
              name: 'Sectors',
              fields: res[1].data,
            },
          ]);
          setAllFilters(res[0].data);
          setShowNoFilterDataImage(false);
        } else {
          setShowNoFilterDataImage(true);
        }
      });
    }
  };

  const fetchBreakoutData = (typeId, value = '', searchText = '') => {
    setAllStocks([]);
    makeApiCall(
      `/foapi/market/fetchMarketAlert?type=${typeId}${!!value ? '&value=' + value : ''}${!!searchText ? '&searchText=' + searchText : ''}${widget ? `&limit=${hideCta ? widgetRows + 5 : widgetRows}` : ''}`,
    ).then((res) => {
      let data = [];
      if (res.status && res.responseCode === 200) {
        !!setShowBreakoutWidget && setShowBreakoutWidget(true);
        res.data.forEach((r) => {
          const temp = {
            name: r.symbol,
            currentPrice: r.lastPrice,
            change: r.change.toFixed(2),
            changePer: r.percentChange.toFixed(2),
            summary: r.description,
            timestamp: getTimeDifference(r.alertTime),
            entityId: r.entityId,
          };
          data.push(temp);
        });
      }
      if ((!!searchText && data.length === 0) || res?.responseCode !== 200 || !res.status) {
        setShowNoDataImage(true);
        if (widget && typeof setShowBreakoutWidget === 'function') {
          setShowBreakoutWidget(false);
        }
      } else {
        setShowNoDataImage(false);
      }
      if (typeof setShowMoreButton === 'function') {
        setShowMoreButton(data.length > 10);
        data = data.slice(0, widgetRows);
      }
      setAllStocks(data);
    });
  };

  const getTimeDifference = (timestamp) => {
    let timeStr = '';
    const timeDiffInMin = Math.abs(new Date() - new Date(timestamp)) / 1000 / 60;
    if (timeDiffInMin > 59) {
      const timeDiffInHr = Math.abs(timeDiffInMin / 60);
      if (timeDiffInHr > 23) {
        const timeDiffInDay = Math.abs(timeDiffInHr / 24);
        timeStr +=
          Math.ceil(timeDiffInDay) + ` ${Math.ceil(timeDiffInDay) === 1 ? 'day' : 'days'} ago`;
      } else {
        timeStr += Math.ceil(timeDiffInHr) + ` ${Math.ceil(timeDiffInHr) === 1 ? 'hr' : 'hrs'} ago`;
      }
    } else {
      timeStr +=
        Math.ceil(timeDiffInMin) + ` ${Math.ceil(timeDiffInMin) === 1 ? 'min' : 'mins'} ago`;
    }

    return timeStr;
  };

  const setFilters = (tab) => {
    inputRefFilterDesktop.current.value = '';
    setCurrentFilterTab(tab);
    if (
      !!currentFilter &&
      !allDataFilters
        .find((filter) => filter.id === tab)
        .fields.find(
          (field) =>
            (field.name || field.eventName) === (currentFilter.name || currentFilter.eventName),
        )
    ) {
      setDisableApply(true);
    } else {
      setDisableApply(false);
    }
    setAllFilters(allDataFilters.find((filter) => filter.id === tab).fields);
  };

  const searchStock = (event) => {
    const str = event.target.value.toLowerCase();
    checkCurrentStatusAndFetch(str);
  };

  const handleSearchClose = () => {
    inputRefDesktop.current.value = '';
    checkCurrentStatusAndFetch();
    setShowNoDataImage(false);
  };

  const handleFilterSearchClose = () => {
    inputRefFilterDesktop.current.value = '';
    setShowNoFilterDataImage(false);
    setAllFilters(allDataFilters.find((filter) => filter.id === currentFilterTab).fields);
  };

  const searchFilter = (event) => {
    const str = event.target.value.toLowerCase();
    const filteredResults = allDataFilters
      .find((filter) => filter.id === currentFilterTab)
      .fields.filter((field) => (field.name || field.eventName).toLowerCase().includes(str));
    if (filteredResults.length === 0) {
      setShowNoFilterDataImage(true);
    } else {
      setShowNoFilterDataImage(false);
    }
    setAllFilters(filteredResults);
  };

  const createFilters = (mode) => (
    <div styleName="filters-ctx" className={`${mode === 'desktop' ? 'desktop-only' : ''}`}>
      <span styleName="filters-title">Filters</span>
      <div styleName="filters-buttons">
        <div
          styleName={`filter ${currentFilterTab === 'entity' ? 'active' : ''} ${showNoFilterDataImage ? 'disable' : ''}`}
          onClick={() => setFilters('entity')}
        >
          Entity Type
        </div>
        <div
          styleName={`filter ${currentFilterTab === 'sectors' ? 'active' : ''} ${showNoFilterDataImage ? 'disable' : ''}`}
          onClick={() => setFilters('sectors')}
        >
          Sectors
        </div>
      </div>
      <div styleName="filter-search">
        <SearchIcon color="regent-gray" fontSize="15px" />
        <input
          type="text"
          ref={inputRefFilterDesktop}
          placeholder="Search"
          styleName="search-filter-input"
          onChange={searchFilter}
        />
        <div styleName="close-search" onClick={handleFilterSearchClose}>
          <CloseIcon color="#B7BFC4" fontSize="14px" />
        </div>
      </div>
      <div styleName="filters">
        {allFilters.length > 0 &&
          allFilters.map((filter, index) => (
            <div
              styleName="filter-item"
              onChange={() => {
                setCurrentFilter(filter);
                setDisableApply(false);
              }}
              key={(filter.name || filter.eventName).replace(/\s/g, '_') + '_' + index}
            >
              <input
                styleName="radio-input"
                type="radio"
                id={(filter.name || filter.eventName).replace(/\s/g, '_')}
                value={(filter.name || filter.eventName).toString()}
                name="filter"
                defaultChecked={
                  (filter.name || filter.eventName) ===
                  (currentFilter.name || currentFilter.eventName)
                }
              />
              <label
                htmlFor={(filter.name || filter.eventName).replace(/\s/g, '_')}
                styleName="filter-name"
              >
                {(filter.name || filter.eventName).length > 20
                  ? (filter.name || filter.eventName).substring(0, 17) + '...'
                  : filter.name || filter.eventName}
              </label>
            </div>
          ))}
        {allFilters.length === 0 && !showNoFilterDataImage && (
          <div styleName="filter-loader">
            <Loader color={'#dadee1'} />
          </div>
        )}
        {showNoFilterDataImage && (
          <div styleName="no-filters-ctx">
            <img src="/images/lighthouse-no-bookmark.svg" alt="" styleName="no-filter-image" />
            <span styleName="no-filter-title">No Filter(s) Found</span>
            <span styleName="no-filter-msg">for your search criteria</span>
          </div>
        )}
      </div>
      <div
        styleName={`apply-filter ${disableApply ? 'disable-click' : ''}`}
        onClick={() => applyFilters()}
      >
        Apply Filter
      </div>
    </div>
  );

  const applyFilters = () => {
    if (isMobile) {
      setFilterFlag(false);
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    }
    inputRefDesktop.current.value = '';
    checkCurrentStatusAndFetch();
  };

  const checkCurrentStatusAndFetch = (str) => {
    const typeId = currentFilterTab === 'entity' ? (currentFilter.id === 0 ? 0 : 1) : 2;
    if (typeId === 0) {
      fetchBreakoutData(typeId, '', str);
    } else {
      fetchBreakoutData(typeId, currentFilter.id, str);
    }
    if (allDataFilters.length === 0) {
      fetchFilters();
    }
  };

  const setFilterFlag = (flag) => {
    setShowFilter(flag);
    if (flag) {
      document.getElementsByTagName('body')[0].style.overflowY = 'hidden';
    } else {
      document.getElementsByTagName('body')[0].style.overflowY = 'scroll';
    }
  };

  return (
    <>
      {!widget && !fullWidget && (
        <div className="budget-breakout-ticker-placeholder">
          <MarketTicker />
        </div>
      )}
      <div
        ref={ref}
        styleName={`breakout-alerts-ctx ${widget ? 'white-bg' : ''} ${darkMode ? 'dark-mode' : ''} ${breakout_alerts.showBanner && !widget ? 'no-pt' : ''} ${(widgetRows === 4 && !lightMode) || fullWidget ? 'widget-mode' : ''} ${!breakout_alerts.showBanner && !widget ? 'mt-30' : ''} ${statsWidget ? 'custom-padding' : ''} ${!!customClass ? customClass : ''} ${fullWidget ? 'full-widget-bg' : ''}`}
      >
        <div
          styleName={`breakout-alerts-inner-ctx ${widget ? 'widget-view' : ''}`}
          className={!widget ? 'container' : ''}
        >
          {!widget && breakout_alerts.showBanner && <BudgetStrip2023 />}
          <div styleName={`title-ctx ${!widget && breakout_alerts.showBanner ? 'mt-25' : ''}`}>
            <NotificationActiveIcon
              fontSize={isMobile ? 'var(--base)' : 'var(--base-medium)'}
              color="social-icon-copy"
            />
            <span styleName="title">{breakout_alerts.title}</span>
          </div>
          <div styleName="content-body">
            {!widget && createFilters('desktop')}
            <div styleName="stocks-ctx">
              {!widget && (
                <div styleName="search-stock-ctx">
                  <div styleName="search-stock">
                    <SearchIcon color="regent-gray" fontSize="16px" />
                    <input
                      type="text"
                      ref={inputRefDesktop}
                      placeholder="Search Stocks, Indices or Commodiities"
                      styleName="search-input"
                      defaultValue={breakoutParams}
                      onChange={searchStock}
                      id="breakout-alerts-search-input"
                    />
                    <div styleName="close-button" onClick={handleSearchClose}>
                      <CloseIcon color="#B7BFC4" fontSize="15px" />
                    </div>
                  </div>
                </div>
              )}
              <div styleName="all-stocks">
                {allStocks.length > 0
                  ? allStocks.map((stock, i) => (
                      <div styleName="stock-ctx" key={i}>
                        <div styleName="stock-name-and-value">
                          <BQLink
                            styleName="stock"
                            href={
                              stock?.entityId === '0'
                                ? widgetLink(`/markets${widgetParam}`, isWidget)
                                : widgetLink(
                                    `/stock/${stock?.entityId}/${stock?.name}${widgetParam}`,
                                    isWidget,
                                  )
                            }
                            target="_blank"
                          >
                            {stock?.change > 0 ? (
                              <img
                                styleName="image"
                                src="/icons/arrow-top-right-thick.svg"
                                alt="Gainers Icon"
                              />
                            ) : (
                              <img
                                styleName="image"
                                src="/icons/arrow-bottom-right-thick.svg"
                                alt="Losers Icon"
                              />
                            )}
                            <div styleName="name">{stock?.name}</div>
                            <div styleName="current-value">{stock?.currentPrice}</div>
                          </BQLink>
                          <div
                            styleName={`change-price ${stock?.change > 0 ? 'positive' : 'negative'}`}
                          >
                            {stock?.change > 0 ? `+${stock?.change}` : stock?.change} (
                            {stock?.changePer > 0 ? `+${stock?.changePer}` : stock?.changePer}%)
                          </div>
                        </div>
                        <div styleName="stock-summary">
                          <div styleName="summary" className="summary">
                            {stock?.summary}
                          </div>
                          <div styleName="timestamp">{stock?.timestamp}</div>
                        </div>
                        <hr styleName="divider-line" />
                      </div>
                    ))
                  : !showNoDataImage &&
                    Array.apply(null, Array(widget ? widgetRows : 5)).map((value, index) => (
                      <div styleName="stock-ctx" key={index}>
                        <div styleName="shimmer-ctx">
                          <div styleName="first-row"></div>
                          <div styleName="second-row">
                            <div styleName="first-part"></div>
                            <div styleName="second-part"></div>
                          </div>
                          <div styleName="third-row"></div>
                        </div>
                        <hr styleName="divider-line" />
                      </div>
                    ))}
                {showNoDataImage && (
                  <div styleName="no-stocks-ctx">
                    <img
                      src="/images/lighthouse-no-bookmark.svg"
                      alt=""
                      styleName="not-found-img"
                    />
                    <span styleName="not-found-title">No Result(s) Found</span>
                    <span styleName="not-found-msg">for the search term you're looking for</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {isMobile && !widget && showMobileFilterButton && (
            <div styleName="mobile-filter" onClick={() => setFilterFlag(true)}>
              <Icon path={mdiTune} size="28px" color="#FFFFFF" />
            </div>
          )}
          {widget && !hideCta && (
            <BQLink
              href={`${widgetRows === 4 ? 'https://www.ndtvprofit.com' : ''}/markets/breakout-alerts${widgetParam}`}
              target="_blank"
            >
              <div
                styleName="view-all-button-ctx"
                onClick={() =>
                  loadMoreInteraction(
                    'View All Breakouts',
                    userData,
                    pageType,
                    breakout_alerts.title,
                  )
                }
              >
                <div styleName="view-all-button">
                  <span styleName="text">View All Breakouts</span>
                  <ArrowRightIcon color={darkMode ? '#FFFFFF' : '#2B2B2B'} />
                </div>
              </div>
            </BQLink>
          )}
        </div>
        {showFilter && (
          <React.Fragment>
            <div styleName="pop-up-filter">{createFilters('mobile')}</div>
            {<div styleName="popover-shadow" onClick={() => setFilterFlag(false)} />}
          </React.Fragment>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  breakout_alerts: get(state.qt, ['config', 'features', 'breakout_alerts'], {}),
});

export default connect(mapStateToProps)(BreakoutAlerts);
